import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';
import { useSnackbar } from 'notistack';
import ImageUploadCrop from '../util/ImageUploadCrop';
import CalendarPicker from '../util/CalendarPicker';

interface Props {
  user: any;              // полные данные текущего пользователя (GET user)
  baseUrl: string;
  onRemindLater: () => void;   // «Заполнить при следующем входе» — session-dismiss
  onSaved: () => void;         // «Сохранить» — profile_completed=true
}

type Gender = { value: string; label_en: string; label_ua: string; label_de: string };

const FirstLoginProfileModal: React.FC<Props> = ({ user, baseUrl, onRemindLater, onSaved }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState<any>({
    first_name: user?.first_name || '', last_name: user?.last_name || '', second_name: user?.second_name || '',
    date_of_birth: user?.date_of_birth ? String(user.date_of_birth).slice(0, 10) : '', sex: user?.sex || '',
    mobile_phone: user?.mobile_phone || '', postcode: user?.postcode || '', city: user?.city || '',
    address: user?.address || '', emergency_contact_name: user?.emergency_contact_name || '',
    emergency_contact_phone: user?.emergency_contact_phone || '', emergency_contact_relationship: user?.emergency_contact_relationship || '',
  });
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ? `${baseUrl}${user.avatarUrl}` : '');
  const [genders, setGenders] = useState<Gender[]>([]);
  const [fieldConfigs, setFieldConfigs] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.allSettled([axios.get('static-gender'), axios.get('hr/profile-field-config')]).then(([g, fc]) => {
      if (g.status === 'fulfilled') setGenders(Array.isArray(g.value.data) ? g.value.data : []);
      if (fc.status === 'fulfilled') setFieldConfigs(Array.isArray(fc.value.data) ? fc.value.data : []);
    });
  }, []);

  const sf = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((p: any) => ({ ...p, [k]: e.target.value }));
  const vis = (k: string) => { const c = fieldConfigs.find(x => x.fieldKey === k); return c ? c.isVisible : true; };
  const genderLabel = (g: Gender) => (i18n.language === 'uk' || i18n.language === 'ua') ? g.label_ua : i18n.language === 'de' ? g.label_de : g.label_en;
  const initials = `${(user?.first_name?.[0] || '')}${(user?.last_name?.[0] || '')}`.toUpperCase();

  const pct = useMemo(() => {
    const keys = ['avatarUrl', 'second_name', 'date_of_birth', 'sex', 'mobile_phone', 'postcode', 'city', 'address', 'emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relationship'].filter(vis);
    if (!keys.length) return 100;
    const filled = keys.filter(k => k === 'avatarUrl' ? !!avatarUrl : String(form[k] ?? '').trim() !== '').length;
    return Math.round((filled / keys.length) * 100);
  }, [form, avatarUrl, fieldConfigs]);

  const handleAvatar = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) { enqueueSnackbar(t('PhotoTooLarge') as string, { variant: 'error' }); return; }
    const fd = new FormData(); fd.append('avatar', file);
    try {
      const { data } = await axios.post('upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAvatarUrl(`${baseUrl}${data.avatarUrl}?t=1`);
    } catch (e: any) { enqueueSnackbar(e?.response?.data?.message || (t('PhotoUploadError') as string), { variant: 'error' }); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`update-user/${user.id}`, {
        ...form,
        date_of_birth: form.date_of_birth || null,
        profile_completed: true,
      });
      enqueueSnackbar(t('SavedSuccessfully') || 'Saved', { variant: 'success' });
      onSaved();
    } catch { enqueueSnackbar(t('ErrorSaving') || 'Error', { variant: 'error' }); }
    finally { setSaving(false); }
  };

  const lbl: React.CSSProperties = { fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-color-calendar-week)' };
  const inp = 'timesheet-edit-input';
  const fld: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };

  return (
    <div data-portal-modal="true"
      style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)' as any, background: 'rgba(10,15,35,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 520, maxWidth: '100%', maxHeight: '90vh', background: 'var(--bg-form)', borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 80px rgba(20,30,70,.35)' }}>
        <div style={{ padding: '18px 20px 14px', color: '#fff', background: 'linear-gradient(135deg,#1e3a8a,#4c1d95 55%,#831843)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <ImageUploadCrop onUpload={handleAvatar} outputWidth={200} outputHeight={200} quality={0.85}>
              <div style={{ width: 62, height: 62, borderRadius: 16, background: avatarUrl ? 'transparent' : 'rgba(255,255,255,.15)', border: '2px solid rgba(255,255,255,.3)', overflow: 'hidden', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800 }}>
                {avatarUrl ? <img src={avatarUrl} alt={initials} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
              </div>
            </ImageUploadCrop>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{t('FillProfileTitle') || 'Complete your profile'}</div>
              <div style={{ fontSize: 12, opacity: .85, marginTop: 2 }}>{t('FillProfileHint') || 'Welcome aboard! Take a minute to complete your details.'}</div>
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, opacity: .9, marginBottom: 4 }}>
              <span>{t('ProfileCompletion') || 'Profile completion'}</span><span>{pct}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 4, background: 'rgba(255,255,255,.2)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: '#fff', borderRadius: 4, transition: 'width .3s' }} />
            </div>
          </div>
        </div>

        <div className="thin-scroll" style={{ padding: 18, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('FirstName')}</span><input className={inp} value={form.first_name} onChange={sf('first_name')} /></div>
            <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('LastName')}</span><input className={inp} value={form.last_name} onChange={sf('last_name')} /></div>
          </div>
          {vis('second_name') && <div style={fld}><span style={lbl}>{t('SecondName')}</span><input className={inp} value={form.second_name} onChange={sf('second_name')} /></div>}
          <div style={{ display: 'flex', gap: 10 }}>
            {vis('date_of_birth') && <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('DateOfBirth') || 'Date of birth'}</span><CalendarPicker mode="single" variant="bare" output="string" className={inp} value={form.date_of_birth} onChange={(v) => setForm((p: any) => ({ ...p, date_of_birth: v }))} /></div>}
            {vis('sex') && <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('Gender')}</span>
              <select title={t('Gender') as string} className={inp} value={form.sex} onChange={sf('sex')}>
                <option value="">—</option>
                {genders.map(g => <option key={g.value} value={g.value}>{genderLabel(g)}</option>)}
              </select></div>}
          </div>
          {vis('mobile_phone') && <div style={fld}><span style={lbl}>{t('MobilePhone') || 'Mobile phone'}</span><input className={inp} value={form.mobile_phone} onChange={sf('mobile_phone')} /></div>}
          <div style={{ display: 'flex', gap: 10 }}>
            {vis('postcode') && <div style={{ ...fld, width: 130 }}><span style={lbl}>{t('Postcode') || 'Postcode'}</span><input className={inp} value={form.postcode} onChange={sf('postcode')} /></div>}
            {vis('city') && <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('City') || 'City'}</span><input className={inp} value={form.city} onChange={sf('city')} /></div>}
          </div>
          {vis('address') && <div style={fld}><span style={lbl}>{t('Address') || 'Address'}</span><input className={inp} value={form.address} onChange={sf('address')} /></div>}

          <div style={{ borderTop: '1px solid var(--border2-new, rgba(0,10,60,.06))', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ ...lbl, fontSize: 11, color: 'var(--text-color-calendar)' }}>{t('EmergencyContact') || 'Emergency contact'}</span>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('Name') || 'Name'}</span><input className={inp} value={form.emergency_contact_name} onChange={sf('emergency_contact_name')} /></div>
              {vis('emergency_contact_relationship') && <div style={{ ...fld, flex: 1 }}><span style={lbl}>{t('HrEcRelationship') || 'Relationship'}</span><input className={inp} value={form.emergency_contact_relationship} onChange={sf('emergency_contact_relationship')} /></div>}
            </div>
            <div style={fld}><span style={lbl}>{t('Phone') || 'Phone'}</span><input className={inp} value={form.emergency_contact_phone} onChange={sf('emergency_contact_phone')} /></div>
          </div>
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border2-new, rgba(0,10,60,.06))', display: 'flex', justifyContent: 'space-between', gap: 8, background: 'var(--surface2)' }}>
          <button type="button" className="dt-btn-ghost" style={{ fontSize: 13 }} onClick={onRemindLater}>{t('RemindNextLogin') || 'Remind me next login'}</button>
          <button type="button" className="btn-primary" style={{ fontSize: 13 }} disabled={saving} onClick={save}>{t('SaveAndClose') || 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginProfileModal;
